import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { ArrowLeft, Landmark, Palmtree, Plane } from 'lucide-react'
import { CurrencyValue, DateValue, ErrorState, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  monthName, PROVISION_TYPE_LABELS, useEmployeeProvisions, useProvisionHistory,
} from '@/features/payroll'
import type { ProvisionType } from '@/features/payroll'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'

const ICONS: Partial<Record<ProvisionType, typeof Landmark>> = {
  GRATUITY: Landmark,
  LEAVE_SALARY: Palmtree,
  AIR_TICKET: Plane,
}

/** What each provision is, in one sentence, so the number has a meaning beside it. */
const EXPLAINS: Partial<Record<ProvisionType, string>> = {
  GRATUITY:
    'End-of-service gratuity accrues on the basic wage for every month of service, and is payable when employment ends.',
  LEAVE_SALARY:
    'Salary payable for leave that has been earned and not taken. It accrues monthly and falls as leave is used.',
  AIR_TICKET:
    'The ticket allowance earned towards the next home-leave entitlement, built up monthly and reduced by each claim.',
}

const VALID: ProvisionType[] = ['GRATUITY', 'LEAVE_SALARY', 'AIR_TICKET']

/**
 * One benefit, in full: what it is worth now, how that figure is arrived at, and every month
 * it moved.
 *
 * <p>The tiles on the profile could only ever show a closing balance and the month it was
 * true on. "Why is it that number" had no answer anywhere in the product — the Provisions
 * report shows one month across everybody, which is the opposite cut of the same data.
 *
 * <p>The header figures come from the balances endpoint and the table from the history
 * endpoint, deliberately: the brought-forward balance is a fact about the person and the
 * provision rather than about any one month, so stamping it on every row of a history would
 * read as if the opening import had happened again and again.
 */
export function BenefitDetailPage() {
  const { id = '', type = '' } = useParams()
  useBreadcrumb()
  const [page, setPage] = useState(0)

  const provisionType = type.toUpperCase() as ProvisionType
  const known = VALID.includes(provisionType)

  const provisions = useEmployeeProvisions(id, known)
  const history = useProvisionHistory(id, known ? provisionType : undefined, page)

  if (!known) {
    return (
      <div className="space-y-4">
        <BackLink id={id} />
        <p className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {type} is not a benefit this product tracks.
        </p>
      </div>
    )
  }

  if (provisions.isPending) return <Skeleton className="h-64" />
  if (provisions.isError) {
    return <ErrorState error={provisions.error} onRetry={() => void provisions.refetch()} />
  }

  const balance = provisions.data.balances.find((row) => row.provisionType === provisionType)
  const Icon = ICONS[provisionType] ?? Landmark
  const rows = history.data?.content ?? []

  return (
    <div className="space-y-4">
      <BackLink id={id} />

      <PageHeader
        title={PROVISION_TYPE_LABELS[provisionType]}
        subtitle={EXPLAINS[provisionType]}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-[var(--color-body)]">Current balance</p>
              <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                {balance
                  ? <CurrencyValue value={balance.closingBalance} currency={balance.currency ?? undefined} />
                  : '—'}
              </p>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                {balance
                  ? `As of end of ${monthName(balance.periodYear, balance.periodMonth)}`
                  : 'Nothing accrued yet. Run the monthly provision accrual to build this balance.'}
              </p>
            </div>
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
              <Icon className="size-5" aria-hidden="true" />
            </span>
          </div>
        </Card>

        <Card className="p-4">
          <p className="text-xs font-medium text-[var(--color-body)]">Last month added</p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
            {balance
              ? <CurrencyValue value={balance.lastAccrual} currency={balance.currency ?? undefined} />
              : '—'}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            The movement in the most recent accrual run.
          </p>
        </Card>

        <Card className="p-4">
          <p className="text-xs font-medium text-[var(--color-body)]">What it accrues on</p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
            {balance?.basisAmount != null
              ? <CurrencyValue value={balance.basisAmount} currency={balance.currency ?? undefined} />
              : '—'}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            {balance?.basisNote ?? 'The basis is recorded with each accrual.'}
          </p>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">Month by month</h3>
          {history.data && (
            <p className="text-xs text-[var(--color-muted)]">
              {history.data.totalElements} month{history.data.totalElements === 1 ? '' : 's'} recorded
            </p>
          )}
        </div>

        {history.isPending && <Skeleton className="m-4 h-40" />}

        {history.isError && (
          <div className="p-4">
            <ErrorState error={history.error} onRetry={() => void history.refetch()} />
          </div>
        )}

        {history.data && rows.length === 0 && (
          <p className="p-6 text-center text-sm text-[var(--color-muted)]">
            No accrual has been recorded for this benefit yet.
          </p>
        )}

        {rows.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
                  <tr>
                    <th className="px-4 py-2 text-start font-medium">Month</th>
                    <th className="px-4 py-2 text-end font-medium">Opening</th>
                    <th className="px-4 py-2 text-end font-medium">Accrued</th>
                    <th className="px-4 py-2 text-end font-medium">Used</th>
                    <th className="px-4 py-2 text-end font-medium">Adjusted</th>
                    <th className="px-4 py-2 text-end font-medium">Closing</th>
                    <th className="px-4 py-2 text-start font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-[var(--color-border)]">
                      <td className="px-4 py-2">{monthName(row.periodYear, row.periodMonth)}</td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        <CurrencyValue value={row.openingBalance} currency={row.currency ?? undefined} />
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        <CurrencyValue value={row.accrual} currency={row.currency ?? undefined} />
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        <CurrencyValue value={row.utilisation} currency={row.currency ?? undefined} />
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        <CurrencyValue value={row.adjustment} currency={row.currency ?? undefined} />
                      </td>
                      <td className="px-4 py-2 text-end font-medium tabular-nums">
                        <CurrencyValue value={row.closingBalance} currency={row.currency ?? undefined} />
                      </td>
                      <td className="px-4 py-2 text-xs text-[var(--color-muted)]">
                        {row.basisNote ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {history.data && history.data.totalPages > 1 && (
              <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] p-3">
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={history.data.first}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  Newer
                </Button>
                <span className="text-xs text-[var(--color-muted)]">
                  Page {history.data.page + 1} of {history.data.totalPages}
                </span>
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={history.data.last}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Older
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {provisionType === 'AIR_TICKET' && provisions.data.airTicketEntitlement && (
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">
            Ticket entitlement in force
          </h3>
          <dl className="grid gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-[var(--color-muted)]">From</dt>
              <dd><DateValue value={provisions.data.airTicketEntitlement.effectiveFrom} /></dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Every</dt>
              <dd>
                {provisions.data.airTicketEntitlement.frequencyMonths} months
                {' · '}
                {provisions.data.airTicketEntitlement.cabinClass.toLowerCase()}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Covers</dt>
              <dd className="tabular-nums">
                {provisions.data.airTicketEntitlement.adultCount} adult
                {provisions.data.airTicketEntitlement.adultCount === 1 ? '' : 's'}
                {provisions.data.airTicketEntitlement.childCount > 0
                  && `, ${provisions.data.airTicketEntitlement.childCount} child`}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Claims recorded</dt>
              <dd className="tabular-nums">{provisions.data.airTicketClaims.length}</dd>
            </div>
          </dl>
        </Card>
      )}
    </div>
  )
}

function BackLink({ id }: { id: string }) {
  return (
    <Link
      to={`/employees/${id}`}
      className="inline-flex items-center gap-1 text-sm text-[var(--color-muted)] hover:underline"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Back to the profile
    </Link>
  )
}
